import { Card } from "@cmsgov/ds-medicare-gov";
import React, { FunctionComponent, useContext } from "react";
import { SpapPlan } from "../../@types";
import { AnalyticsActionType } from "../../app/contexts/Analytics/types";
import { AppContext } from "../../app/store";
import { formatStringPhone, useTranslate } from "../../helpers";
import { addProtocolToUrl } from "../../helpers/objectUtilities";

interface SpapPlanCardProps {
  plan: SpapPlan;
}

const getLimitedPharmaciesRestriction = (
  plan: SpapPlan
): string | undefined => {
  const { limited_pharmacies } = plan.restrictions;
  if (limited_pharmacies) {
    return "spap.restriction.limited_pharmacies";
  }

  return undefined;
};

const getPlanEligibilityRequirements = (plan: SpapPlan): string[] => {
  const {
    age,
    income,
    asset,
    lis,
    other_factors,
    hiv_aids,
    part_d_enrollment,
  } = plan.restrictions;
  const eligibilityRequirements: string[] = [];

  if (part_d_enrollment) {
    eligibilityRequirements.push("spap.restriction.part_d");
  }

  if (hiv_aids) {
    eligibilityRequirements.push("spap.restriction.hiv_aids");
  }

  if (age) {
    eligibilityRequirements.push("spap.restriction.age");
  }

  if (income) {
    eligibilityRequirements.push("spap.restriction.income");
  }

  if (asset) {
    eligibilityRequirements.push("spap.restriction.asset");
  }

  if (lis) {
    eligibilityRequirements.push("spap.restriction.lis");
  }

  if (other_factors) {
    eligibilityRequirements.push("spap.restriction.other_factors");
  }

  return eligibilityRequirements;
};

const SpapPlanCard: FunctionComponent<SpapPlanCardProps> = ({ plan }) => {
  const t = useTranslate();

  const limitedPharmaciesRestriction = getLimitedPharmaciesRestriction(plan);
  const { dispatch } = useContext(AppContext);
  const requirements = getPlanEligibilityRequirements(plan);

  return (
    <Card className="SpapPlanCard ds-u-padding--2 ds-u-margin-top--3">
      <div className="ds-l-row">
        <div className="SpapPlanCard__cardColumn ds-l-col--12 ds-l-md-col--6 ds-u-border-right--1">
          <div className="SpapPlanCard__cardColumnSection">
            <h2 className="ds-u-margin-bottom--1" id={plan.program_name}>
              {plan.program_name}
            </h2>
            {limitedPharmaciesRestriction && (
              <p className="ds-u-color--gray ds-u-margin-top--0">
                {t(limitedPharmaciesRestriction)}
              </p>
            )}
          </div>
          <div className="SpapPlanCard__cardColumnSection">
            <h3 className="SpapPlanCard__sectionHeading">
              {t("pap.plans.contact_information")}
            </h3>
            <div className="SpapPlanCard__contact">
              {plan.phone1 && (
                <>
                  <span className="ds-u-font-weight--semibold">
                    {t("pap.plans.phone")}
                  </span>{" "}
                  <a
                    href={`tel:${plan.phone1}`}
                    onClick={() => {
                      dispatch({
                        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                        settings: {
                          event_action: "Phone Click",
                          event_label: `${plan.state}: ${plan.program_name} ${plan.phone1}`,
                          other_props: { event_category: "SPAP" },
                        },
                      });
                    }}
                  >
                    {formatStringPhone(plan.phone1)}
                  </a>
                </>
              )}
            </div>
            {plan.url && (
              <>
                <a
                  href={addProtocolToUrl(plan.url)}
                  aria-describedby={plan.program_name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mct-hide-print ds-u-margin-top--2 ds-c-button"
                  onClick={() => {
                    dispatch({
                      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                      settings: {
                        event_action: "External Link Click",
                        event_label: `View program website: ${plan.url}`,
                        other_props: { event_category: "SPAP" },
                      },
                    });
                  }}
                >
                  {t("pap.plans.view_program_website")}
                </a>
                <div className="mct-print-only ds-u-margin-top--2">
                  {plan.url}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="SpapPlanCard__cardColumn ds-u-margin-top--2 ds-u-md-margin-top--0 ds-l-col--12 ds-l-md-col--6">
          <div className="ds-u-padding-left--0 ds-u-md-padding-left--1">
            <h3 className="SpapPlanCard__sectionHeading">
              {t("pap.plans.eligibility_criteria")}
            </h3>
            {requirements.length > 0 && (
              <ul className="ds-c-list mct-disc-list" role="list">
                {requirements.map((req, index) => (
                  <li role="listitem" key={index}>
                    {t(req)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SpapPlanCard;
